import { reactive } from 'vue';

/*
 * DIÁLOGOS DO SISTEMA — o substituto de `window.confirm`, `window.alert` e `window.prompt`.
 *
 * REGRA DO PROJETO: as janelas nativas do navegador NUNCA são usadas. Elas não seguem o visual do
 * sistema, não têm o texto dos botões em português ("OK"/"Cancelar" dependem do navegador),
 * bloqueiam a página inteira (inclusive timers e o SignalR), o Chrome passa a oferecer "impedir
 * esta página de criar outras caixas de diálogo" — e, marcada essa opção, o `confirm` passa a
 * devolver `false` em silêncio e a ação nunca acontece.
 *
 * Tudo passa por AQUI, e o desenho é o `ModalConsulte` (via `DialogoSistema.vue`, montado uma
 * única vez no `App.vue`): foco preso, Esc, devolução do foco e trava de rolagem já vêm dele.
 *
 *   if (!(await dialogoService.confirmar({ mensagem: 'Excluir o registro?', perigo: true }))) return;
 *   await dialogoService.avisar({ mensagem: 'Selecione ao menos uma turma.' });
 *   const senha = await dialogoService.perguntar({ mensagem: 'Informe a senha', tipoCampo: 'password' });
 *
 * Funciona fora de componente (serviço, composable, guarda de rota) porque o estado é um módulo.
 * Pedidos feitos com um diálogo já aberto entram na FILA e aparecem em seguida — nenhum é perdido.
 */

export type TipoDialogo = 'confirmar' | 'avisar' | 'perguntar';
export type TomDialogo = 'info' | 'sucesso' | 'aviso' | 'erro';

export interface OpcoesConfirmar {
  titulo?: string;
  mensagem: string;
  /** Texto do botão que confirma. Prefira o verbo da ação: "Excluir", "Descartar". */
  textoConfirmar?: string;
  textoCancelar?: string;
  /** Ação destrutiva: botão de confirmar vermelho e o foco inicial no CANCELAR. */
  perigo?: boolean;
}

export interface OpcoesAvisar {
  titulo?: string;
  mensagem: string;
  tom?: TomDialogo;
  textoOk?: string;
}

export interface OpcoesPerguntar {
  titulo?: string;
  mensagem: string;
  /** Rótulo do campo (acessível). Sem ele, a própria mensagem é o rótulo. */
  rotulo?: string;
  valorInicial?: string;
  tipoCampo?: 'text' | 'password' | 'number' | 'email';
  obrigatorio?: boolean;
  textoConfirmar?: string;
  textoCancelar?: string;
  /** Validação própria: devolve a mensagem de erro, ou vazio quando o valor serve. */
  validar?: (valor: string) => string | undefined | null;
}

interface PedidoDialogo {
  tipo: TipoDialogo;
  titulo: string;
  mensagem: string;
  tom: TomDialogo;
  textoConfirmar: string;
  textoCancelar: string;
  perigo: boolean;
  rotulo: string;
  valor: string;
  tipoCampo: string;
  obrigatorio: boolean;
  validar?: (valor: string) => string | undefined | null;
  resolver: (resposta: any) => void;
}

/** O estado lido pelo `DialogoSistema.vue`. Só este módulo escreve nele. */
export const estadoDialogo = reactive({
  atual: null as PedidoDialogo | null,
  fila: [] as PedidoDialogo[],
});

function enfileirar(pedido: PedidoDialogo) {
  if (estadoDialogo.atual) estadoDialogo.fila.push(pedido);
  else estadoDialogo.atual = pedido;
}

/** Chamado pelo componente quando o usuário responde (ou fecha pelo Esc/X). */
export function responderDialogo(resposta: any) {
  const pedido = estadoDialogo.atual;

  estadoDialogo.atual = estadoDialogo.fila.shift() ?? null;

  pedido?.resolver(resposta);
}

class DialogoService {
  /** Pergunta sim/não. Fechar pelo Esc ou pelo X vale como NÃO. */
  confirmar(opcoes: OpcoesConfirmar | string): Promise<boolean> {
    const o = typeof opcoes === 'string' ? { mensagem: opcoes } : opcoes;

    return new Promise((resolver) =>
      enfileirar({
        tipo: 'confirmar',
        titulo: o.titulo ?? (o.perigo ? 'Confirme a ação' : 'Confirmação'),
        mensagem: o.mensagem,
        tom: o.perigo ? 'aviso' : 'info',
        textoConfirmar: o.textoConfirmar ?? 'Confirmar',
        textoCancelar: o.textoCancelar ?? 'Cancelar',
        perigo: Boolean(o.perigo),
        rotulo: '',
        valor: '',
        tipoCampo: 'text',
        obrigatorio: false,
        resolver: (resposta) => resolver(resposta === true),
      }),
    );
  }

  /** Mensagem com um botão só. A promessa resolve quando o usuário fecha. */
  avisar(opcoes: OpcoesAvisar | string): Promise<void> {
    const o = typeof opcoes === 'string' ? { mensagem: opcoes } : opcoes;
    const tom = o.tom ?? 'info';
    const titulos: Record<TomDialogo, string> = {
      info: 'Informação',
      sucesso: 'Tudo certo',
      aviso: 'Atenção',
      erro: 'Não foi possível concluir',
    };

    return new Promise((resolver) =>
      enfileirar({
        tipo: 'avisar',
        titulo: o.titulo ?? titulos[tom],
        mensagem: o.mensagem,
        tom,
        textoConfirmar: o.textoOk ?? 'Entendi',
        textoCancelar: '',
        perigo: false,
        rotulo: '',
        valor: '',
        tipoCampo: 'text',
        obrigatorio: false,
        resolver: () => resolver(),
      }),
    );
  }

  /** Pede um valor. Devolve o texto digitado, ou `null` se o usuário cancelar. */
  perguntar(opcoes: OpcoesPerguntar): Promise<string | null> {
    return new Promise((resolver) =>
      enfileirar({
        tipo: 'perguntar',
        titulo: opcoes.titulo ?? 'Informe',
        mensagem: opcoes.mensagem,
        tom: 'info',
        textoConfirmar: opcoes.textoConfirmar ?? 'Confirmar',
        textoCancelar: opcoes.textoCancelar ?? 'Cancelar',
        perigo: false,
        rotulo: opcoes.rotulo ?? '',
        valor: opcoes.valorInicial ?? '',
        tipoCampo: opcoes.tipoCampo ?? 'text',
        obrigatorio: Boolean(opcoes.obrigatorio),
        validar: opcoes.validar,
        resolver: (resposta) => resolver(typeof resposta === 'string' ? resposta : null),
      }),
    );
  }
}

export default new DialogoService();
